import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getOptimizedMapboxOptions, setupMapboxErrorHandling, initializeMapbox, CUSTOM_NAVIGATION_STYLE } from '@/lib/mapbox-config';

// Initialize Mapbox with optimized settings
initializeMapbox();

interface MapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  className?: string;
  interactive?: boolean;
  markerTitle?: string;
  // Map visual style. Defaulting to 'dark' for better theme match on detail pages
  style?: 'navigation' | 'dark' | 'light' | 'streets' | 'satellite' | 'terrain';
}

const Map: React.FC<MapProps> = ({
  latitude,
  longitude,
  zoom = 13,
  className = "h-[250px] w-full rounded-md overflow-hidden",
  interactive = true,
  markerTitle,
  style = 'dark'
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    try {
      // Resolve style URL
      const styleUrl = (() => {
        switch (style) {
          case 'navigation':
            return CUSTOM_NAVIGATION_STYLE || 'mapbox://styles/mapbox/navigation-day-v1';
          case 'streets':
            return 'mapbox://styles/mapbox/streets-v12';
          case 'satellite':
            return 'mapbox://styles/mapbox/satellite-streets-v12';
          case 'terrain':
            return 'mapbox://styles/mapbox/outdoors-v12';
          case 'dark':
            return 'mapbox://styles/mapbox/dark-v11';
          case 'light':
            return 'mapbox://styles/mapbox/light-v11';
          default:
            return 'mapbox://styles/mapbox/dark-v11';
        }
      })();

      // Initialize map with resolved style
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: styleUrl,
        center: [longitude, latitude],
        zoom: zoom,
        interactive: interactive,
        // Apply telemetry blocking and optimizations
        transformRequest: getOptimizedMapboxOptions(mapContainer.current, styleUrl).transformRequest,
        collectResourceTiming: false,
        attributionControl: false,
        antialias: false,
        maxTileCacheSize: 200
      });

      // Setup error handling to prevent console noise
      setupMapboxErrorHandling(map.current);

      // Add marker
      marker.current = new mapboxgl.Marker({
        color: '#059669' // emerald-600 - works well with navigation style
      })
        .setLngLat([longitude, latitude])
        .addTo(map.current);

      // Add popup if title provided
      if (markerTitle) {
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(`<div class="font-medium text-gray-900">${markerTitle}</div>`);
        
        marker.current.setPopup(popup);
      }

      // Add navigation controls if interactive
      if (interactive) {
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      }

    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Error initializing map:', error);
      }
    }

    // Cleanup function
    return () => {
      if (marker.current) {
        marker.current.remove();
      }
      if (map.current) {
        map.current.remove();
      }
    };
  }, []);

  // Update marker position when coordinates change
  useEffect(() => {
    if (marker.current && map.current) {
      marker.current.setLngLat([longitude, latitude]);
      map.current.setCenter([longitude, latitude]);
    }
  }, [latitude, longitude]);

  // Update zoom when zoom prop changes
  useEffect(() => {
    if (map.current) {
      map.current.setZoom(zoom);
    }
  }, [zoom]);

  return (
    <div 
      ref={mapContainer} 
      className={className}
      style={{ position: 'relative' }}
    />
  );
};

export default Map;
